/* eslint no-undef: "off" */
import "./assets/main.css";
import "./assets/vars.css";

import { createRouterScroller } from "vue-router-better-scroller";
import i18n from "@/locales/i18n";
import { initAuth } from "@/hooks/auth";
import { initBackRules } from "@/hooks/backRules";
import { initRouterRules } from "@/hooks/routerRules";
import { initRestrictions } from "@/hooks/initRestrictions";
import { api } from "@/api";
import { createApiFacade } from "@/api/apiFacade";
import WA from "@/miniapp/tg";

import UUID from "vue3-uuid";

import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/ru";
import * as Sentry from "@sentry/vue";

dayjs.locale("ru");
dayjs.extend(duration);
dayjs.extend(relativeTime);

import { createApp } from "vue";
import { createPinia } from "pinia";

import App from "./App.vue";
import router from "./router";
import store from "./store";
import { useUserStore } from "@/stores/user";
import { useExchangeStore } from "@/stores/exchange";
import { useP2PStore } from "@/stores/p2p.js";
import { useWithdrawStore } from "@/stores/withdraw.js";
import { useCoinholdStore } from "@/stores/coinhold.js";
import { useStakingStore } from "@/stores/staking.js";
import { useUiStore } from "@/stores/ui.js";
import { useCoins } from "@/composables/useCoins";
import TelegramAnalytics from "@telegram-apps/analytics";
import AnalyticsService from "@emcd-shared/analytics-service";

// Dev only: in production globalThis.import_meta_env is populated at container start by
// import-meta-env. In local dev the placeholder stays an unparsed string, so libs that read
// globalThis.import_meta_env (e.g. @emcd-shared/feature-flag-service) can't resolve VITE_* vars.
// Mirror Vite's import.meta.env into it so feature flags work locally.
if (import.meta.env.DEV && typeof globalThis.import_meta_env !== "object") {
  globalThis.import_meta_env = { ...import.meta.env };
  // BaseApiFacade (@emcd-shared/api-service) reads VUE_APP_ENDPOINT_API_URL; в dev
  // index.html-mirror не срабатывает (плейсхолдер — строка), поэтому даём алиас тут,
  // ДО инстанцирования ApiFacade. Endpoint = VITE_API_ENDPOINT.
  if (!globalThis.import_meta_env.VUE_APP_ENDPOINT_API_URL) {
    globalThis.import_meta_env.VUE_APP_ENDPOINT_API_URL =
      import.meta.env.VITE_API_ENDPOINT;
  }
}

TelegramAnalytics.init({
  token:
    globalThis?.import_meta_env?.VITE_TON_ANALYTICS_SDK_KEY ||
    import.meta.env.VITE_TON_ANALYTICS_SDK_KEY,
  appName:
    globalThis?.import_meta_env?.VITE_TON_SDK_PROJECT_NAME ||
    import.meta.env.VITE_TON_SDK_PROJECT_NAME,
});

// Фолбэк на фатальный сбой инициализации транспорта (ApiFacade): рисуем
// статичный экран ошибки прямо в #app вместо молча-нерабочего приложения.
// i18n ещё не смонтирован — текст двуязычный, без зависимостей.
function renderBootError() {
  const el = document.getElementById("app");
  if (!el) return;
  el.textContent = "";
  const box = document.createElement("div");
  box.style.cssText =
    "padding:24px;text-align:center;font-family:sans-serif;line-height:1.5;";
  const title = document.createElement("h2");
  title.textContent = "Не удалось запустить приложение";
  const text = document.createElement("p");
  text.textContent =
    "Failed to start the app. Please reopen it. / Перезапустите приложение.";
  box.append(title, text);
  el.appendChild(box);
}

function initVueApp() {
  const app = createApp(App);

  // Sentry инициализируем ДО createApiFacade — иначе фатальный сбой фасада
  // (renderBootError ниже) не долетит: captureException до init дропается.
  if (import.meta.env.MODE !== "development") {
    Sentry.init({
      app,
      dsn:
        globalThis?.import_meta_env?.VITE_SENTRY_DSN ||
        import.meta.env.VITE_SENTRY_DSN,
      integrations: [Sentry.captureConsoleIntegration()],
      // Сторонние скрипты (GTM/Segment/reCAPTCHA) логируют в console.error
      // DOMException от AbortSignal.timeout() на медленной сети. В нашем коде
      // AbortSignal.timeout не используется (таймауты axios дают другой текст).
      ignoreErrors: [/signal timed out/],
    });
  }

  window.Intercom = function nope() {};
  app.config.globalProperties.$AnalyticsService = AnalyticsService; // this.$AnalyticsService
  app.provide(
    "$AnalyticsService",
    app.config.globalProperties.$AnalyticsService,
  ); // useAnalyticsService()

  app.use(createPinia());
  app.use(store); // Vuex

  // axios-фасад — единственный транспорт. Без него КАЖДЫЙ запрос падает
  // "api-facade-not-ready" → приложение нерабочее. Сбой фасада фатальный:
  // логируем и показываем экран ошибки, boot не продолжаем.
  let apiFacade;
  try {
    apiFacade = createApiFacade(app);
  } catch (e) {
    AnalyticsService.error(`ApiFacade init failed: ${e?.message || e}`);
    Sentry.captureException(e, {
      tags: { boot: "api-facade-init" },
    });
    renderBootError();
    return;
  }

  api.init(
    {
      user: useUserStore(),
      exchange: useExchangeStore(),
      p2p: useP2PStore(),
      withdraw: useWithdrawStore(),
      coinhold: useCoinholdStore(),
      staking: useStakingStore(),
      ui: useUiStore(),
      // coin-service адаптер (композабл над Vuex) — инжектим через init, чтобы
      // модули не импортили useCoins напрямую в обход конвенции.
      coins: useCoins(),
    },
    apiFacade,
  );

  app.use(router);

  app.use(
    createRouterScroller({
      window: true,
      selectors: {
        ".scrollable"({ to, from, type, savedPosition, element }) {
          if (type === "push") {
            if (to.meta.scrollTop) {
              return { top: 0 };
            }
            if (from.meta.topLevel) {
              return { top: 0 };
            }
            return false;
          }
          if (type === "history") {
            if (from.meta.topLevel) {
              return { top: 0 };
            }
            element.scrollTo({
              ...savedPosition,
            });
          }
        },
      },
    }),
  );
  app.use(UUID);

  initAuth();
  initBackRules();
  initRouterRules();
  WA.init();
  app.use(i18n);
  app.mount("#app");

  // Ждём завершения первой навигации (и её guard'ов, включая tmaAuth в initAuth) —
  // иначе на чистой сессии запрос статуса рестрикшена уходит без токена и молча падает в 401.
  router.isReady().then(() => initRestrictions(app));
}

initVueApp();
